/**
 * Narrowing the bootstrap's filter options to the current selection (US-22).
 *
 * The page used to ask the server which repositories, area paths and coverage
 * components were in scope every time the programme selection changed. The
 * bootstrap response now carries each option's scope with it, so the same
 * question is answered locally — which is what lets the page hold to two
 * requests however often the user changes a filter.
 *
 * All of it is pure and string-in, string-out, so the narrowing rule can be
 * tested without a component, a query client or a server.
 */

import type { FilterOption, FilterOptions } from '@delivery-metrics/shared';

export interface FilterScope {
  units?: string[];
  programs?: string[];
}

/**
 * A selection, in the vocabulary the scope keys are written in.
 *
 * The page stores **display** values — "Unit A" — because that is what the
 * dropdowns show and what the URL has always carried. The scope keys on each
 * option are **canonical** — "unit-a" — because that is what the builder
 * stored. Comparing the two directly silently matches nothing, which is the
 * worst possible failure here: every dropdown narrows to the unscoped
 * options and the page looks like it is working.
 *
 * So a selection is translated through the dimension's own options before it
 * is used as a scope. A value that matches no label is passed through
 * unchanged — it may already be canonical, and inventing a translation for it
 * would be worse than letting it fail to match.
 */
export function toCanonical(
  selected: readonly string[] | undefined,
  dimensionOptions: readonly FilterOption[] | undefined
): string[] {
  const byLabel = new Map(
    (dimensionOptions ?? []).map(option => [
      option.label.toLowerCase(),
      option.value,
    ])
  );
  return (selected ?? []).map(
    value => byLabel.get(value.toLowerCase()) ?? value
  );
}

/** The current selection as canonical scope keys. */
export function canonicalScope(
  options: FilterOptions | null | undefined,
  selection: FilterScope
): FilterScope {
  return {
    units: toCanonical(selection.units, options?.unit),
    programs: toCanonical(selection.programs, options?.program),
  };
}

/** The shape the page's existing prune logic already expects. */
export interface ScopedDataSources {
  repositories: string[];
  areaPaths: string[];
  sonarqubeProjects: string[];
}

const lower = (values: readonly string[]): Set<string> =>
  new Set(values.map(value => value.toLowerCase()));

const intersects = (
  optionScope: readonly string[],
  selected: Set<string>
): boolean => optionScope.some(value => selected.has(value.toLowerCase()));

/**
 * Is this option available under the current selection?
 *
 * Two rules, and the second is the one worth stating out loud:
 *
 * 1. **Nothing selected in a dimension means no narrowing by it.** An empty
 *    programme selection is "all programmes", exactly as the server treats it.
 * 2. **An option with no scope of its own is always in scope.** The builder
 *    could not attribute it to a unit or programme; hiding it would shrink the
 *    dropdown silently, and a missing repository looks identical to one that
 *    was never collected. Showing it may offer a filter that matches nothing —
 *    visibly empty results, which the user can act on.
 *
 * Comparison is case-insensitive, matching the page's existing prune, because
 * canonical values and the selections made against display values do not
 * always agree on case.
 */
export function isOptionInScope(
  option: FilterOption,
  scope: FilterScope
): boolean {
  const units = lower(scope.units ?? []);
  const programs = lower(scope.programs ?? []);

  const unitOk =
    units.size === 0 ||
    option.units.length === 0 ||
    intersects(option.units, units);
  const programOk =
    programs.size === 0 ||
    option.programs.length === 0 ||
    intersects(option.programs, programs);

  return unitOk && programOk;
}

/** The display values of every option available under *scope*. */
export function scopedLabels(
  options: readonly FilterOption[] | undefined,
  scope: FilterScope
): string[] {
  return (options ?? [])
    .filter(option => isOptionInScope(option, scope))
    .map(option => option.label);
}

/**
 * The three data-source dropdowns, scoped — the local replacement for
 * `POST /api/metrics/data-sources`.
 *
 * `sonarqubeProjects` comes from the `coverageComponent` dimension: the
 * dropdown is named for SonarQube but the dimension covers TICS too, which is
 * why the contract does not call it `sonarqubeProject`.
 */
export function scopedDataSources(
  options: FilterOptions | null | undefined,
  selection: FilterScope
): ScopedDataSources {
  const scope = canonicalScope(options, selection);
  return {
    repositories: scopedLabels(options?.repository, scope),
    areaPaths: scopedLabels(options?.areaPath, scope),
    sonarqubeProjects: scopedLabels(options?.coverageComponent, scope),
  };
}

/**
 * The programmes available under the selected units, as display values.
 *
 * Empty selection means every programme, matching how the server treats an
 * empty filter.
 */
export function scopedPrograms(
  options: FilterOptions | null | undefined,
  selectedUnits: readonly string[] | undefined
): string[] {
  return scopedLabels(options?.program, {
    units: toCanonical(selectedUnits, options?.unit),
  });
}

/**
 * Unit **display** value to the programme display values under it.
 *
 * Keyed by display value because that is what the page's selections hold;
 * built from the programme options' canonical `units`, translated back
 * through the unit options. One source, two spellings, reconciled here rather
 * than at four call sites.
 */
export function unitProgramMapByLabel(
  options: FilterOptions | null | undefined
): Record<string, string[]> {
  const unitLabelByValue = new Map(
    (options?.unit ?? []).map(option => [option.value, option.label])
  );
  const map: Record<string, string[]> = {};
  for (const program of options?.program ?? []) {
    for (const unit of program.units) {
      const label = unitLabelByValue.get(unit) ?? unit;
      const programs = map[label] ?? [];
      programs.push(program.label);
      map[label] = programs;
    }
  }
  return map;
}
